(() => {
  const DESKTOP_QUERY = '(min-width: 750px)';
  const GALLERY_MATCH = 'media-gallery.media-gallery--carousel';
  const MEDIA_ROOT_SELECTOR = '.product-information__media';
  const SLIDE_SELECTOR = 'slideshow-component slideshow-slides > slideshow-slide';
  const THUMB_SELECTOR = "slideshow-controls[thumbnails][pagination-position='left'] .slideshow-controls__thumbnail";
  const COLOR_OPTION_NAMES = new Set(['color', 'colour']);
  const COLOR_TARGET_VIEWPORT_RATIO = 0.43;
  const COLOR_SCROLL_DURATION = 160;
  const COLOR_CHANGE_DEDUP_MS = 700;

  const desktopQuery = window.matchMedia(DESKTOP_QUERY);
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mainRoot = document.getElementById('MainContent');
  const eventRoot = mainRoot || document;

  const isDesktop = () => desktopQuery.matches;
  const prefersReducedMotion = () => reducedMotionQuery.matches;
  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

  const getGalleries = () =>
    Array.from(eventRoot.querySelectorAll?.(GALLERY_MATCH) || []).filter(
      (gallery) => !gallery.closest('quick-add-dialog'),
    );

  const getGalleryParts = (gallery) => {
    const slides = Array.from(gallery.querySelectorAll(SLIDE_SELECTOR)).filter((slide) => !slide.hidden);
    const thumbs = Array.from(gallery.querySelectorAll(THUMB_SELECTOR));
    const thumbRail = gallery.querySelector('.slideshow-controls__thumbnails-container');
    return { slides, thumbs, thumbRail };
  };

  const getSlideMediaId = (slide) =>
    slide?.querySelector('.product-media[data-media-id]')?.getAttribute('data-media-id') || '';

  const getSlideAlt = (slide) => {
    const image = slide?.querySelector('.product-media img, img');
    return image instanceof HTMLImageElement ? image.alt.trim() : '';
  };

  const getColorOrder = () => {
    const inputs = Array.from(eventRoot.querySelectorAll?.('.product-details input[data-option-name]') || []);
    const colors = [];

    inputs.forEach((input) => {
      const optionName = normalize(input.dataset.optionName);
      if (!COLOR_OPTION_NAMES.has(optionName)) return;
      const value = normalize(input.value);
      if (value && !colors.includes(value)) colors.push(value);
    });

    return colors;
  };

  const parseColorImageAlt = (alt) => {
    const match = String(alt || '').trim().match(/^(.+?)-(\d+)$/i);
    if (!match) return null;
    return {
      color: normalize(match[1]),
      position: Number.parseInt(match[2], 10),
    };
  };

  // Reorder the desktop product gallery by explicit Alt Text convention:
  // Black-1, Black-2, Coffee-1, Coffee-2, Brown-1, Brown-2, ...
  // The color-group order follows the actual Color option order in Shopify.
  const reorderGalleryByAlt = (gallery) => {
    if (!isDesktop() || !gallery?.closest('#MainContent') || gallery.closest('quick-add-dialog')) return false;

    const { slides, thumbs } = getGalleryParts(gallery);
    if (slides.length < 2) return false;

    const colorOrder = getColorOrder();
    if (!colorOrder.length) return false;

    const slideshow = gallery.querySelector(':scope > slideshow-component') || gallery.querySelector('slideshow-component');
    const initialIndex = Number.parseInt(slideshow?.getAttribute('initial-slide') || '0', 10);
    const initialMediaId = Number.isFinite(initialIndex) ? getSlideMediaId(slides[initialIndex]) : '';

    const colorIndex = new Map(colorOrder.map((color, index) => [color, index]));
    let matchedCount = 0;

    const entries = slides.map((slide, index) => {
      const parsed = parseColorImageAlt(getSlideAlt(slide));
      const knownColor = parsed && colorIndex.has(parsed.color);
      if (knownColor) matchedCount += 1;

      return {
        slide,
        thumb: thumbs[index] || null,
        originalIndex: index,
        groupIndex: knownColor ? colorIndex.get(parsed.color) : Number.MAX_SAFE_INTEGER,
        imagePosition: knownColor && Number.isFinite(parsed.position) ? parsed.position : Number.MAX_SAFE_INTEGER,
      };
    });

    // Require at least two explicit color-position Alt Text matches before changing order.
    if (matchedCount < 2) return false;

    entries.sort((a, b) => {
      if (a.groupIndex !== b.groupIndex) return a.groupIndex - b.groupIndex;
      if (a.imagePosition !== b.imagePosition) return a.imagePosition - b.imagePosition;
      return a.originalIndex - b.originalIndex;
    });

    const orderChanged = entries.some((entry, index) => entry.originalIndex !== index);
    if (!orderChanged) {
      gallery.dataset.kervelanAltOrdered = 'true';
      return false;
    }

    const slideParent = slides[0]?.parentElement;
    const thumbParent = thumbs[0]?.parentElement;
    if (!slideParent) return false;

    entries.forEach((entry, index) => {
      slideParent.appendChild(entry.slide);

      // Zoom actions are index based, so keep them aligned with the new order.
      const clickAction = entry.slide.getAttribute('on:click');
      if (clickAction && /\/open\/\d+/.test(clickAction)) {
        entry.slide.setAttribute('on:click', clickAction.replace(/\/open\/\d+/, `/open/${index}`));
      }

      if (entry.thumb && thumbParent) {
        thumbParent.appendChild(entry.thumb);
        entry.thumb.setAttribute('on:click', `/select/${index}`);
      }
    });

    if (slideshow instanceof HTMLElement && initialMediaId) {
      const reorderedInitialIndex = entries.findIndex((entry) => getSlideMediaId(entry.slide) === initialMediaId);
      if (reorderedInitialIndex >= 0) slideshow.setAttribute('initial-slide', String(reorderedInitialIndex));
    }

    gallery.dataset.kervelanAltOrdered = 'true';
    return true;
  };

  const findSlideIndexByMediaId = (gallery, mediaId) => {
    if (!mediaId) return -1;
    const { slides } = getGalleryParts(gallery);
    return slides.findIndex((slide) => getSlideMediaId(slide) === String(mediaId));
  };

  const getHeaderOffset = () => {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getTriggerY = () => {
    const headerOffset = getHeaderOffset();
    return headerOffset + (window.innerHeight - headerOffset) * 0.5;
  };

  const setActiveThumb = (gallery, index, smooth = false) => {
    const { thumbs, thumbRail } = getGalleryParts(gallery);
    if (!thumbs.length || index < 0 || index >= thumbs.length) return;

    thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === index;
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      thumb.classList.toggle('is-scroll-active', active);
      thumb.tabIndex = active ? 0 : -1;
    });

    const activeThumb = thumbs[index];
    if (!activeThumb || !thumbRail) return;

    const railRect = thumbRail.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();
    const outsideTop = thumbRect.top < railRect.top + 2;
    const outsideBottom = thumbRect.bottom > railRect.bottom - 2;

    if (outsideTop || outsideBottom) {
      const targetTop =
        activeThumb.offsetTop - Math.max(0, (thumbRail.clientHeight - activeThumb.offsetHeight) / 2);

      thumbRail.scrollTo({
        top: Math.max(0, targetTop),
        behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
      });
    }
  };

  const findActiveSlideIndex = (gallery) => {
    const { slides } = getGalleryParts(gallery);
    if (!slides.length) return -1;

    const triggerY = getTriggerY();
    let activeIndex = 0;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      if (rect.top <= triggerY) activeIndex = index;
    });

    return activeIndex;
  };

  let frame = 0;
  const syncAll = () => {
    frame = 0;
    if (!isDesktop()) return;

    getGalleries().forEach((gallery) => {
      const index = findActiveSlideIndex(gallery);
      if (index >= 0) setActiveThumb(gallery, index, false);
    });
  };

  const scheduleSync = () => {
    if (frame) return;
    frame = requestAnimationFrame(syncAll);
  };

  let scrollAnimationFrame = 0;
  let initialPositionPending = true;

  const cancelActiveScroll = () => {
    initialPositionPending = false;
    if (!scrollAnimationFrame) return;
    cancelAnimationFrame(scrollAnimationFrame);
    scrollAnimationFrame = 0;
  };

  const fastScrollTo = (targetTop, duration = COLOR_SCROLL_DURATION) => {
    cancelActiveScroll();

    const startTop = window.scrollY;
    const destination = Math.max(0, targetTop);
    const distance = destination - startTop;

    if (prefersReducedMotion() || Math.abs(distance) < 2 || duration <= 0) {
      window.scrollTo(0, destination);
      return;
    }

    const startTime = performance.now();
    const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);

    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      window.scrollTo(0, startTop + distance * easeOutCubic(progress));

      if (progress < 1) {
        scrollAnimationFrame = requestAnimationFrame(step);
      } else {
        scrollAnimationFrame = 0;
      }
    };

    scrollAnimationFrame = requestAnimationFrame(step);
  };

  const scrollSlideToPosition = (
    gallery,
    index,
    animate = true,
    viewportRatio = 0.5,
    duration = COLOR_SCROLL_DURATION,
  ) => {
    const { slides } = getGalleryParts(gallery);
    const target = slides[index];
    if (!target) return false;

    setActiveThumb(gallery, index, animate);

    const rect = target.getBoundingClientRect();
    const headerOffset = getHeaderOffset();
    const usableHeight = Math.max(0, window.innerHeight - headerOffset);
    const targetViewportY = headerOffset + usableHeight * viewportRatio;
    const targetDocumentY = window.scrollY + rect.top + rect.height / 2 - targetViewportY;

    if (animate) {
      fastScrollTo(targetDocumentY, duration);
    } else {
      cancelActiveScroll();
      window.scrollTo(0, Math.max(0, targetDocumentY));
    }

    return true;
  };

  const positionInitialVariant = () => {
    if (!initialPositionPending) return;
    initialPositionPending = false;

    const navigation = performance.getEntriesByType('navigation')[0];
    if (
      !isDesktop() ||
      window.location.hash ||
      (navigation?.type === 'reload' && window.scrollY > getHeaderOffset()) ||
      navigation?.type === 'back_forward' ||
      !new URLSearchParams(window.location.search).has('variant')
    ) return;

    const gallery = getGalleries()[0];
    const slideshow = gallery?.querySelector('slideshow-component');
    const index = Number.parseInt(slideshow?.getAttribute('initial-slide') || '0', 10);
    if (index > 0) scrollSlideToPosition(gallery, index, false, COLOR_TARGET_VIEWPORT_RATIO);
  };

  const getColorOptionElement = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    if (target instanceof HTMLSelectElement) {
      const option = target.options[target.selectedIndex];
      return option || target;
    }

    if (target instanceof HTMLInputElement && target.matches('input[data-option-name]')) {
      return target;
    }

    const label = target.closest('.variant-option__button-label');
    const input = label?.querySelector('input[data-option-name]');
    return input instanceof HTMLInputElement ? input : null;
  };

  const isProductColorTarget = (target) => {
    if (!(target instanceof Element)) return false;
    const input =
      target instanceof HTMLInputElement && target.matches('input[data-option-name]')
        ? target
        : target.closest('.variant-option__button-label')?.querySelector('input[data-option-name]');

    if (!(input instanceof HTMLInputElement)) return false;
    if (!input.closest('.product-details')) return false;
    return COLOR_OPTION_NAMES.has(normalize(input.dataset.optionName));
  };

  let lastPointerMediaId = '';
  let lastPointerAt = 0;

  // Mouse/touch input starts the gallery move on pointerdown, before Horizon's
  // asynchronous variant refresh. The later change event remains as a keyboard
  // and select fallback but is deduplicated so one selection only moves once.
  const markColorSelection = (event, source = 'change') => {
    if (!isDesktop()) return;

    const optionElement = getColorOptionElement(event);
    if (!(optionElement instanceof Element)) return;
    if (!optionElement.closest('.product-details')) return;

    const optionName = normalize(optionElement.dataset.optionName);
    if (!COLOR_OPTION_NAMES.has(optionName)) return;

    const mediaId = optionElement.dataset.featuredMediaId || '';
    if (!mediaId) return;

    const now = performance.now();
    if (
      source === 'change' &&
      mediaId === lastPointerMediaId &&
      now - lastPointerAt < COLOR_CHANGE_DEDUP_MS
    ) {
      return;
    }

    const gallery = getGalleries()[0];
    if (!gallery) return;

    const index = findSlideIndexByMediaId(gallery, mediaId);
    if (index < 0) return;

    if (source === 'pointerdown') {
      lastPointerMediaId = mediaId;
      lastPointerAt = now;
    }

    scrollSlideToPosition(
      gallery,
      index,
      true,
      COLOR_TARGET_VIEWPORT_RATIO,
      COLOR_SCROLL_DURATION,
    );
  };

  const findAddedGallery = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(GALLERY_MATCH)) return node;
        const nestedGallery = node.querySelector?.(GALLERY_MATCH);
        if (nestedGallery) return nestedGallery;
      }
    }
    return null;
  };

  eventRoot.addEventListener(
    'pointerdown',
    (event) => {
      initialPositionPending = false;
      if (isProductColorTarget(event.target)) {
        markColorSelection(event, 'pointerdown');
      } else {
        cancelActiveScroll();
      }
    },
    true,
  );

  eventRoot.addEventListener('change', (event) => markColorSelection(event, 'change'), true);

  eventRoot.addEventListener(
    'click',
    (event) => {
      if (!isDesktop() || !(event.target instanceof Element)) return;

      const thumb = event.target.closest(THUMB_SELECTOR);
      if (!thumb) return;

      const gallery = thumb.closest(GALLERY_MATCH);
      if (!gallery) return;

      const { thumbs } = getGalleryParts(gallery);
      const index = thumbs.indexOf(thumb);
      if (index < 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      scrollSlideToPosition(gallery, index, true, 0.5, COLOR_SCROLL_DURATION);
    },
    true,
  );

  // Any explicit user navigation wins over the short automatic move. Color
  // swatch pointerdown is excluded because it is itself the navigation command.
  window.addEventListener('wheel', cancelActiveScroll, { passive: true });
  window.addEventListener('touchstart', cancelActiveScroll, { passive: true });
  eventRoot.addEventListener(
    'keydown',
    (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) {
        cancelActiveScroll();
      }
    },
    true,
  );

  window.addEventListener('scroll', scheduleSync, { passive: true });
  window.addEventListener('resize', scheduleSync, { passive: true });

  const observer = new MutationObserver((mutations) => {
    if (!isDesktop()) return;

    const newGallery = findAddedGallery(mutations);
    if (!newGallery) return;

    reorderGalleryByAlt(newGallery);
    scheduleSync();
  });

  let observedMediaRoot = null;
  const observeProductMedia = () => {
    const nextMediaRoot = eventRoot.querySelector?.(MEDIA_ROOT_SELECTOR);
    if (!nextMediaRoot || nextMediaRoot === observedMediaRoot) return;

    observer.disconnect();
    observedMediaRoot = nextMediaRoot;
    observer.observe(nextMediaRoot, {
      childList: true,
      subtree: true,
    });
  };

  const reorderCurrentGalleries = () => {
    getGalleries().forEach(reorderGalleryByAlt);
    scheduleSync();
  };

  const init = () => {
    observeProductMedia();
    reorderCurrentGalleries();
    requestAnimationFrame(positionInitialVariant);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  desktopQuery.addEventListener?.('change', (event) => {
    if (event.matches) reorderCurrentGalleries();
    else cancelActiveScroll();
  });

  document.addEventListener('shopify:section:load', () => {
    observeProductMedia();
    reorderCurrentGalleries();
  });
})();
